import { ArrowBack, ArrowForward, Check, Close, DevicesOther } from "@mui/icons-material";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslate } from "react-admin";

import { borderRadius, color, shadows, space } from "../../theme/designTokens";
import { deviceEnrollmentPhases } from "./simulationData";

type DeviceEnrollmentWizardProps = {
  deviceCount: number;
  onClose: () => void;
  onShowDevices: () => void;
  open: boolean;
};

export const DeviceEnrollmentWizard = ({ deviceCount, onClose, onShowDevices, open }: DeviceEnrollmentWizardProps) => {
  const translate = useTranslate();
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [shotIndex, setShotIndex] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setPhaseIndex(0);
      setShotIndex(0);
      setDone(false);
    }
  }, [open]);

  const phase = deviceEnrollmentPhases[phaseIndex];
  const shot = phase.shots[shotIndex];

  const goToPhase = (nextPhaseIndex: number) => {
    if (nextPhaseIndex <= phaseIndex) {
      setPhaseIndex(nextPhaseIndex);
      setShotIndex(0);
      setDone(false);
    }
  };

  const goBack = () => {
    if (done) {
      const lastPhaseIndex = deviceEnrollmentPhases.length - 1;
      setDone(false);
      setPhaseIndex(lastPhaseIndex);
      setShotIndex(deviceEnrollmentPhases[lastPhaseIndex].shots.length - 1);
      return;
    }

    if (shotIndex > 0) {
      setShotIndex(current => current - 1);
      return;
    }

    if (phaseIndex > 0) {
      const previousPhaseIndex = phaseIndex - 1;
      setPhaseIndex(previousPhaseIndex);
      setShotIndex(deviceEnrollmentPhases[previousPhaseIndex].shots.length - 1);
    }
  };

  const goNext = () => {
    if (done) {
      onClose();
      return;
    }

    if (shotIndex < phase.shots.length - 1) {
      setShotIndex(current => current + 1);
      return;
    }

    if (phaseIndex < deviceEnrollmentPhases.length - 1) {
      setPhaseIndex(current => current + 1);
      setShotIndex(0);
      return;
    }

    setDone(true);
  };

  const canGoBack = done || phaseIndex > 0 || shotIndex > 0;
  const nextLabel = done
    ? translate("onboarding.device.close")
    : phaseIndex === deviceEnrollmentPhases.length - 1 && shotIndex === phase.shots.length - 1
      ? translate("onboarding.device.finish")
      : shotIndex === phase.shots.length - 1
        ? translate("onboarding.device.nextPhase")
        : translate("onboarding.device.nextImage");

  return (
    <Dialog
      aria-labelledby="device-enrollment-title"
      data-testid="device-enrollment-wizard"
      fullWidth
      maxWidth={false}
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          borderRadius: borderRadius.md,
          boxShadow: shadows.dialog,
          height: "calc(100% - 28px)",
          m: 0,
          maxHeight: "none",
          maxWidth: "none",
          overflow: "hidden",
          width: "calc(100% - 28px)",
        },
      }}
    >
      <Box sx={{ alignItems: "center", display: "flex", gap: space.xs, px: space.sm, py: space.xs }}>
        <Box
          sx={{
            alignItems: "center",
            backgroundColor: color.selectedOptionBackground,
            borderRadius: borderRadius.md,
            color: color.primary,
            display: "flex",
            height: 40,
            justifyContent: "center",
            width: 40,
          }}
        >
          <DevicesOther />
        </Box>
        <Box>
          <Typography id="device-enrollment-title" variant="h6">
            {translate("onboarding.device.title")}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {done
              ? translate("onboarding.device.doneStep")
              : translate("onboarding.device.step", {
                  current: phaseIndex + 1,
                  total: deviceEnrollmentPhases.length,
                })}
          </Typography>
        </Box>
        <IconButton
          aria-label={translate("onboarding.device.close")}
          onClick={onClose}
          sx={{ backgroundColor: color.background2, ml: "auto" }}
        >
          <Close />
        </IconButton>
      </Box>

      <Box
        aria-label={translate("onboarding.device.progress")}
        sx={{
          borderBottom: `1px solid ${color.panelBorder}`,
          display: "flex",
          gap: space.xs,
          px: space.sm,
          pb: space.xs,
        }}
      >
        {deviceEnrollmentPhases.map((progressPhase, index) => {
          const isComplete = done || index < phaseIndex;
          const isActive = !done && index === phaseIndex;
          return (
            <Button
              aria-current={isActive ? "step" : undefined}
              disabled={index > phaseIndex}
              key={progressPhase.labelKey}
              onClick={() => goToPhase(index)}
              sx={{
                "&.Mui-disabled": {
                  backgroundColor: color.transparent,
                  color: color.secondary40,
                },
                alignItems: "stretch",
                color: isActive ? color.typographyBase : color.secondary40,
                display: "flex",
                flex: 1,
                flexDirection: "column",
                gap: space.xxs,
                minWidth: 0,
                p: 0,
                textAlign: "left",
                textTransform: "none",
              }}
            >
              <Box
                sx={{
                  backgroundColor: isComplete || isActive ? color.primary : color.progressTrack,
                  borderRadius: borderRadius.lg,
                  height: 5,
                  width: "100%",
                }}
              />
              <Box sx={{ alignItems: "center", display: "flex", gap: space.xxs, width: "100%" }}>
                <Box
                  sx={{
                    alignItems: "center",
                    backgroundColor: isComplete || isActive ? color.primary : color.progressTrack,
                    borderRadius: borderRadius.lg,
                    color: isComplete || isActive ? color.white : color.secondary40,
                    display: "flex",
                    flexShrink: 0,
                    height: 24,
                    justifyContent: "center",
                    width: 24,
                  }}
                >
                  {isComplete ? <Check fontSize="small" /> : index + 1}
                </Box>
                <Typography noWrap sx={{ display: { xs: "none", sm: "block" } }} variant="body2">
                  {translate(progressPhase.labelKey)}
                </Typography>
              </Box>
            </Button>
          );
        })}
      </Box>

      <DialogContent sx={{ minHeight: 0, p: space.sm }}>
        {done ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={space.sm}
            sx={{ height: "100%", textAlign: "center" }}
          >
            <Box
              sx={{
                alignItems: "center",
                backgroundColor: color.plannedChipBackground,
                borderRadius: borderRadius.lg,
                color: color.success,
                display: "flex",
                height: 72,
                justifyContent: "center",
                width: 72,
              }}
            >
              <Check fontSize="large" />
            </Box>
            <Box sx={{ maxWidth: 680 }}>
              <Typography variant="h4">{translate("onboarding.device.doneTitle")}</Typography>
              <Typography color="text.secondary" sx={{ mt: space.xs }}>
                {translate("onboarding.device.doneDescription")}
              </Typography>
            </Box>
            <Alert severity="info" sx={{ maxWidth: 680 }}>
              {translate("onboarding.device.simulationResult", { count: deviceCount })}
            </Alert>
            <Button onClick={onShowDevices} variant="outlined">
              {translate("onboarding.device.showDevices")}
            </Button>
          </Stack>
        ) : (
          <Stack spacing={space.sm}>
            <Box>
              <Typography variant="h5">{translate(phase.labelKey)}</Typography>
              <Typography color="text.secondary" sx={{ mt: space.xxs }}>
                {translate(phase.introKey)}
              </Typography>
            </Box>
            <Box
              sx={{
                alignItems: "start",
                display: "grid",
                gap: space.sm,
                gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 0.85fr) minmax(420px, 1.15fr)" },
              }}
            >
              <Stack spacing={space.xxs}>
                {phase.shots.map((phaseShot, index) => (
                  <Button
                    aria-current={index === shotIndex ? "step" : undefined}
                    key={phaseShot.titleKey}
                    onClick={() => setShotIndex(index)}
                    sx={{
                      alignItems: "flex-start",
                      backgroundColor: index === shotIndex ? color.selectedOptionBackground : color.white,
                      border: `1px solid ${index === shotIndex ? color.selectedOptionBorder : color.panelBorder}`,
                      borderRadius: borderRadius.md,
                      color: color.typographyBase,
                      display: "flex",
                      gap: space.xs,
                      justifyContent: "flex-start",
                      p: space.xs,
                      textAlign: "left",
                      textTransform: "none",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: index === shotIndex ? color.primary : color.progressTrack,
                        borderRadius: borderRadius.lg,
                        flexShrink: 0,
                        height: 10,
                        mt: space.xxs,
                        width: 10,
                      }}
                    />
                    <Box>
                      <Typography fontWeight="medium">{translate(phaseShot.titleKey)}</Typography>
                      <Typography color="text.secondary" variant="body2">
                        {translate(phaseShot.descriptionKey)}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Stack>
              <Stack spacing={space.xs}>
                <Box
                  sx={{
                    backgroundColor: color.typographyBase,
                    borderRadius: borderRadius.md,
                    boxShadow: shadows.dialog,
                    p: space.xs,
                  }}
                >
                  <Box
                    alt={translate(shot.titleKey)}
                    component="img"
                    src={shot.image}
                    sx={{
                      aspectRatio: "5 / 3",
                      borderRadius: borderRadius.sm,
                      display: "block",
                      objectFit: "cover",
                      width: "100%",
                    }}
                  />
                </Box>
                <Typography align="center" color="text.secondary" variant="body2">
                  {translate("onboarding.device.imageCounter", {
                    current: shotIndex + 1,
                    total: phase.shots.length,
                  })}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: color.background2,
          borderTop: `1px solid ${color.panelBorder}`,
          justifyContent: "space-between",
          px: space.sm,
          py: space.xs,
        }}
      >
        <Button disabled={!canGoBack} onClick={goBack} startIcon={<ArrowBack />} variant="outlined">
          {translate("onboarding.navigation.back")}
        </Button>
        <Button endIcon={done ? undefined : <ArrowForward />} onClick={goNext} variant="contained">
          {nextLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
